<template>
  <content-dialog
    v-model="sendToClientsDialog"
    fullscreen-mobile
    width="600px"
    title="Send Message"
    persistent
    :tags="[helpCodes.appointmentMessages, helpCodes.appointments]"
    @hide="reset"
  >
    <q-list>
      <sticky-header>
        Recipients
      </sticky-header>

      <page-banner
        v-if="!wantsToSendNotification"
        color="red-7"
        dark
        dense
        class="q-mx-sm"
        icon="mdi-exclamation"
        label="Select at least one recipient to send this message to"
      />

      <q-list separator>
        <EditableReminderList
          v-for="(client, key) in clients"
          :key="key"
          :reminders="getReminderForClient(client.id)"
          :email-suggestions="$store.getters['clientSuggestionLoader/getEmails'](client.id)"
          :phone-suggestions="$store.getters['clientSuggestionLoader/getPhones'](client.id)"
          :default-email="baseSelectAppropriateClientDetail('EMAIL', client)"
          :default-phone="baseSelectAppropriateClientDetail('SMS', client)"
          add-label="Add Details"
          real-time
          hide-row-title
          email-prefill-hint="Email has been prefilled from client profile"
          phone-prefill-hint="Mobile has been prefilled from client profile"
          @add="addReminderForClient(client)"
          @remove="removeReminderForClient"
          @email-focus="$store.dispatch('clientSuggestionLoader/loadEmails', client.id)"
          @phone-focus="$store.dispatch('clientSuggestionLoader/loadPhones', client.id)"
        >
          <template #model>
            <client-item
              :client="client"
              dense
              without-email
              without-address
            />
          </template>
        </EditableReminderList>
      </q-list>

      <template v-if="willSendSMS">
        <q-card-section>
          <SmsChargeWarning />
        </q-card-section>

        <SandboxSmsNotice v-if="isDevelopment" class="q-mx-sm" />
      </template>
    </q-list>

    <q-separator spaced inset />

    <q-list>
      <sticky-header>
        Message Type
      </sticky-header>

      <q-item-wrapper tag="label">
        <q-radio
          v-model="messageType"
          val="DEFAULT"
        >
          <q-item-label class="text-bold">
            Default message for this appointment status ({{ statusSettings.name }})
          </q-item-label>
          <q-item-label>
            {{ statusSettings.default_message }}
          </q-item-label>
        </q-radio>
      </q-item-wrapper>

      <q-item-wrapper tag="label">
        <q-radio
          v-model="messageType"
          val="CUSTOM"
          :disable="!wantsToSendNotification"
        >
          <q-item-label class="text-bold">
            {{ customMessageStr }}
          </q-item-label>
          <q-item-label>
            Write your own message. Custom messages will automatically contain the client's name and your business eCard details.
          </q-item-label>
        </q-radio>
      </q-item-wrapper>

      <q-item-wrapper
        v-if="showCustomSmsBox"
        label="SMS Contents"
        label-class="text-bold"
      >
        <q-input
          v-model="customSms"
          autogrow
          outlined
          dense
          placeholder="Enter SMS message..."
          counter
          :maxlength="500"
          :error="!!errors['custom_email']"
          :error-message="!!errors['custom_email'] ? errors['custom_email'][0] : null"
        />
      </q-item-wrapper>

      <q-item-wrapper
        v-if="showCustomEmailBox"
        label="Email Contents"
        label-class="text-bold"
      >
        <q-field
          :error="!!errors['custom_email']"
          :error-message="!!errors['custom_email'] ? errors['custom_email'][0] : null"
          borderless
          dense
          hide-bottom-space
          color="black"
          tag="div"
        >
          <template #control>
            <text-editor
              v-model="customEmail"
              placeholder="Enter email message..."
            />
          </template>
        </q-field>
      </q-item-wrapper>
    </q-list>

    <template #bottom>
      <q-toolbar>
        <q-space />
        <q-btn
          v-close-popup
          flat
          color="blue-grey"
          label="Cancel"
        />
        <q-btn
          label="Send Message"
          color="green"
          :disable="!wantsToSendNotification"
          @click="sendNotification"
        />
      </q-toolbar>
    </template>
  </content-dialog>
</template>

<script>
/*
  Send a manual notification to appointment clients
 */
import EditableReminderList from 'components/partials/communications/reminders/EditableReminderList'
import SandboxSmsNotice from 'components/partials/sandbox/SandboxSmsNotice'
import SmsChargeWarning from 'components/partials/communications/SmsChargeWarning'
import fillClientDetailsMixin from './mixins/fillClientDetailsMixin'
import statusMixin from './mixins/statusMixin'
import { createReminder } from 'components/partials/communications/communicationMixin'

export default {
  components: {
    EditableReminderList, SandboxSmsNotice, SmsChargeWarning
  },
  mixins: [fillClientDetailsMixin, statusMixin],
  props: {
    id: {
      type: Number,
      required: true
    },
    status: {
      type: String,
      required: true
    },
    clients: {
      type: Array,
      default: () => {
        return []
      }
    }
  },
  data () {
    return {
      isDevelopment: !!process.env.SANDBOX,
      sendToClientsDialog: false,
      reminders: [],
      messageType: 'DEFAULT',
      customEmail: null,
      customSms: null,
      errors: {}
    }
  },
  computed: {
    statusSettings () {
      return this.baseStatusSettings(this.status)
    },
    wantsToSendNotification () {
      return this.reminders.filter(item => item.type !== 'NONE').length > 0
    },
    willSendSMS () {
      return this.reminders.filter(item => item.type === 'SMS').length > 0
    },
    willSendEmail () {
      return this.reminders.filter(item => item.type === 'EMAIL').length > 0
    },
    showCustomEmailBox () {
      return this.messageType === 'CUSTOM' && this.willSendEmail
    },
    showCustomSmsBox () {
      return this.messageType === 'CUSTOM' && this.willSendSMS
    },
    customMessageStr () {
      const arr = []
      if (this.willSendSMS) {
        arr.push('SMS')
      }
      if (this.willSendEmail) {
        arr.push('Message')
      }
      if (arr.length < 1) {
        return 'Custom Message'
      }
      return `Custom ${arr.join(' & ')}`
    }
  },
  methods: {
    open () {
      for (const client of this.clients) {
        if (client.disable_reminders) {
          continue
        }
        this.addReminderForClient(client)
      }
      this.sendToClientsDialog = true
    },
    reset () {
      this.reminders = []
      this.messageType = 'DEFAULT'
      this.customEmail = null
      this.customSms = null
      this.errors = {}
    },
    addReminderForClient (client) {
      this.reminders.push(createReminder({
        client_id: client.id,
        type: client.default_communication_method,
        key: 'message'
      }))
    },
    removeReminderForClient ({ value }) {
      this.reminders.splice(this.reminders.indexOf(value), 1)
    },
    getReminderForClient (id) {
      return this.reminders.filter(item => item.client_id === id)
    },
    async sendNotification () {
      this.$loading.show({ message: 'Sending message...' })
      this.errors = {}
      const { status, errors } = await this.$post('v1/appointments/notify', {
        id: this.id,
        message_type: this.messageType,
        reminders: this.reminders,
        custom_email: this.messageType === 'CUSTOM' ? this.customEmail : null,
        custom_sms: this.messageType === 'CUSTOM' ? this.customSms : null
      })
      this.$loading.hide()
      if (status === 'success') {
        this.$q.notify({
          color: 'green',
          message: 'Appointment message successfully sent'
        })
        this.sendToClientsDialog = false
      }
      else {
        this.errors = errors
        this.$nextTick(() => { this.scrollToFirstError(this.$el) })
      }
    }
  }
}
</script>
